import { useMeeting,usePubSub } from "@videosdk.live/react-sdk";
import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import ParticipantViewer from "./ParticipantViewer";
import PresenterView from "./PresenterView";
import {
  getGridRowsAndColumns,
  getGridForMainParticipants,
  calcQuality,
  localAndPinnedOnTop,
  meetingResolutions,
} from "../../utils/common";
import useIsMobile from "../../utils/useIsMobile";
import useIsTab from "../../utils/useIsTab";
import useIsSMDesktop from "../../utils/useIsSMDesktop";
import useIsLGDesktop from "../../utils/useIsLGDesktop";
import {
  meetingLayouts,
  appThemes,
  useMeetingAppContext,
} from "../../MeetingAppContextDef";
import { animated } from 'react-spring';
import { Box, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useResponsiveSize from "../../utils/useResponsiveSize";
import { useMediaQuery } from "react-responsive";
import WhiteboardContainer, {
  convertHWAspectRatio,
} from "../../components/whiteboard/WhiteboardContainer";
import { useMotionParticipantAnimation, useMountAnimation, getAnimationDuration } from "../../utils/animationUtils";
import { getParticipantContainerPadding, PARTICIPANT_CONTAINER_SIZE, LAYOUT_GUTTER } from "../../utils/styleUtils";
import { 
  calculatePaddingStyles, 
  calculateSidebarOffset, 
  calculateInnerWidth,
  calculateWhiteboardDimensions,
  getQualitySetting,
  calculateSpotlightFlex,
  calculateSidebarFlex
} from "../../utils/layoutCalculations";
import { 
  createBannerUpdateHandlers, 
  createBackgroundUpdateHandlers, 
  createOverlayUpdateHandlers,
  createMusicUpdateHandlers,
  createLogoUpdateHandlers
} from "../../utils/pubSubUtils";
import { calculatePresenterSidebarWidth } from "../../utils/responsiveUtils";

// Helper function to convert pixel values to vh
const pxToVh = (px) => `${(px / 10).toFixed(1)}vh`;

// Helper function to convert pixel values to vw
const pxToVw = (px) => `${(px / 10).toFixed(1)}vw`;

// Helper function to convert numeric spacing to vh for use in calc()
const spacingToVh = (spacing) => `${(spacing / 10).toFixed(1)}vh`;

export const PaddingTop = (meetingOverlay, bannerName, isRecorder) => {
  if (meetingOverlay || bannerName) {
    return isRecorder ? "7%" : "5%";
  }
  return "0%";
};

const MemoizedParticipant = React.memo(
  ParticipantViewer,
  (
    { participantId, quality, useVisibilitySensor },
    {
      participantId: oldParticipantId,
      quality: oldQuality,
      useVisibilitySensor: oldUseVisibilitySensor,
    }
  ) =>
    participantId === oldParticipantId &&
    quality === oldQuality &&
    useVisibilitySensor === oldUseVisibilitySensor
);

const MotionParticipant = ({
  participantId,
  gutter,
  quality,
  relativeHeight,
  relativeWidth,
  relativeTop,
  relativeLeft,
  useVisibilitySensor,
  meetingOverlay,
  meetingBackground,
  participantCount,
  isScreenSharing
}) => {
  const mounted = useMountAnimation();
  const { animationsEnabled } = useMeetingAppContext();

  const animatedProps = useMotionParticipantAnimation({
    relativeTop,
    relativeLeft,
    relativeHeight,
    relativeWidth,
    mounted,
    animationsEnabled
  });

  if (!animatedProps) {
    return null;
  }

  return (
    <animated.div
      style={{
        position: 'absolute',
        top: animatedProps.top.interpolate((val) => `${val}%`),
        left: animatedProps.left.interpolate((val) => `${val}%`),
        height: animatedProps.height.interpolate((val) => `${val}%`),
        width: animatedProps.width.interpolate((val) => `${val}%`),
        ...getParticipantContainerPadding(!!meetingBackground, participantCount, isScreenSharing),
        transform: animatedProps.scale.interpolate((val) => `scale(${val})`),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={PARTICIPANT_CONTAINER_SIZE}>
        <MemoizedParticipant
          participantId={participantId}
          quality={quality}
          useVisibilitySensor={useVisibilitySensor}
        />
      </div>
    </animated.div>
  );
};

MotionParticipant.propTypes = {
  participantId: PropTypes.string,
  gutter: PropTypes.number,
  quality: PropTypes.string,
  relativeHeight: PropTypes.number,
  relativeWidth: PropTypes.number,
  relativeTop: PropTypes.number,
  relativeLeft: PropTypes.number,
  useVisibilitySensor: PropTypes.bool,
  meetingOverlay: PropTypes.string,
  meetingBackground: PropTypes.string,
  participantCount: PropTypes.number,
  isScreenSharing: PropTypes.bool,
};

const MotionParticipantContainer = ({
  participantId,
  gutter,
  quality,
  relativeHeight: height,
  relativeWidth: width,
  relativeTop: top,
  relativeLeft: left,
  useVisibilitySensor,
}) => {
  const { animationsEnabled } = useMeetingAppContext();
  const mMeeting = useMeeting();
  const participantCount = mMeeting?.participants?.size || 1;
  const isScreenSharing = !!mMeeting?.presenterId;
  console.log("animationsEnabled", animationsEnabled);
  const [meetingOverlay, setMeetingOverlay] = useState("");
  const [meetingBackground, setMeetingBackground] = useState("");

  usePubSub("UPDATE_MEETING_OVERLAY", {
    onMessageReceived: (data) => {
      const { message } = data;
      setMeetingOverlay(message?.OverlayUrl);
      console.log(meetingOverlay);
    },
    onOldMessagesReceived: (messages) => {
      if (messages && messages.length > 0) {
        const latestMessage = messages[messages.length - 1].message.OverlayUrl;
        console.log(latestMessage)
        setMeetingOverlay(latestMessage);
      }
    },
  });

  usePubSub("UPDATE_MEETING_BACKGROUND", {
    onMessageReceived: (data) => {
      const { message } = data;
      setMeetingBackground(message?.BackgroundUrl);
    },
    onOldMessagesReceived: (messages) => {
      if (messages && messages.length > 0) {
        const latestMessage = messages[messages.length - 1].message.BackgroundUrl;
        setMeetingBackground(latestMessage);
      }
    },
  });

  return animationsEnabled ? (
    <MotionParticipant
      {...{
        participantId,
        gutter,
        quality,
        relativeHeight: height,
        relativeWidth: width,
        relativeTop: top,
        relativeLeft: left,
        useVisibilitySensor,
        meetingOverlay,
        meetingBackground,
        participantCount,
        isScreenSharing
      }}
    />
  ) : (
    <div
      style={{
        position: "absolute",
        top: `${top}%`,
        left: `${left}%`,
        height: `${height}%`,
        width: `${width}%`,
        ...getParticipantContainerPadding(!!meetingBackground, participantCount, isScreenSharing),
      }}
    >
      <div style={PARTICIPANT_CONTAINER_SIZE}>
        <MemoizedParticipant
          {...{ participantId, quality, useVisibilitySensor }}
        />
      </div>
    </div>
  );
};

MotionParticipantContainer.propTypes = MotionParticipant.propTypes;

export const MemoizedMotionParticipant = React.memo(
  MotionParticipantContainer,
  (prevProps, nextProps) =>
    prevProps.participantId === nextProps.participantId &&
    prevProps.gutter === nextProps.gutter &&
    prevProps.quality === nextProps.quality &&
    prevProps.relativeHeight === nextProps.relativeHeight &&
    prevProps.relativeWidth === nextProps.relativeWidth &&
    prevProps.relativeTop === nextProps.relativeTop &&
    prevProps.relativeLeft === nextProps.relativeLeft &&
    prevProps.useVisibilitySensor === nextProps.useVisibilitySensor
);

const MainViewContainer = ({
  height,
  width,
  whiteboardToolbarWidth,
  whiteboardSpacing,
  left
}) => {
  const mMeeting = useMeeting();
  const sideBarData = useSelector((state) => state?.sidebar);
  const participants = mMeeting?.participants;
  const presenterId = mMeeting?.presenterId;
  const localParticipantId = mMeeting?.localParticipant?.id;
  const pinnedParticipants = mMeeting?.pinnedParticipants;
  const activeSpeakerId = mMeeting?.activeSpeakerId;
  const mainParticipantId = mMeeting?.mainParticipant?.id;
  const transcriptionState = mMeeting?.transcriptionState;
  const [meetingBackground, setMeetingBackground] = useState("");
  const [bannerName, setBannerName] = useState('');
  const [bannerId, setBannerId] = useState(null);
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const isSMDesktop = useIsSMDesktop();
  const isLGDesktop = useIsLGDesktop();

  const isPortrait = useMediaQuery({ query: "(orientation: portrait)" });

  const rowSpacing = useResponsiveSize({
    xl: 24,
    lg: 16,
    md: 14,
    sm: 12,
    xs: 8,
  });

  const gutter = LAYOUT_GUTTER; // Changed from '0.4vh' to numeric value for calculations

  const {
    mainViewParticipants,
    sideBarMode,
    meetingLayout,
    whiteboardStarted,
    activeSortedParticipants,
    animationsEnabled,
    layoutGridSize,
    hideLocalParticipant,
    sideStackSize,
    reduceEdgeSpacing,
    mode,
    appTheme,
    meetingResolution,
    isRecorder,
  } = useMeetingAppContext();

  const lastActiveParticipantId = useMemo(
    () => activeSortedParticipants[0]?.participantId,
    [activeSortedParticipants]
  );

  const mobilePortrait = isMobile && isPortrait;

  const {
    spotlightParticipantId,
    sideBarPinnedParticipantIds,
    spotlightMediaType
  } = useMemo(() => {
    let pinnedParticipantIds = [...pinnedParticipants.keys()];

    const pinnedPresenter =
      pinnedParticipantIds.findIndex((id) => id === presenterId) !== -1;

    if (isRecorder && pinnedParticipantIds.length > layoutGridSize) {
      if (pinnedPresenter) {
        const pinnedParticipantIndexToBeRemoved =
          pinnedParticipantIds.findIndex((id) => id === presenterId);

        pinnedParticipantIds.splice(pinnedParticipantIndexToBeRemoved, 1);

        pinnedParticipantIds = [pinnedPresenter, ...pinnedParticipantIds];
      }

      pinnedParticipantIds = pinnedParticipantIds.slice(0, layoutGridSize);
    }

    let obj;

    if (pinnedPresenter) {
      obj = {
        spotlightParticipantId: presenterId,
        sideBarPinnedParticipantIds:
          meetingLayout === meetingLayouts.SPOTLIGHT
            ? []
            : pinnedParticipantIds,
        spotlightMediaType: "SHARE",
      };
    } else if (whiteboardStarted) {
      if (meetingLayout === meetingLayouts.SPOTLIGHT) {
        obj = {
          spotlightParticipantId: "white-board-id",
          sideBarPinnedParticipantIds: [],
          spotlightMediaType: "WHITEBOARD",
        };
      } else {
        obj = {
          spotlightParticipantId: "white-board-id",
          sideBarPinnedParticipantIds: pinnedParticipantIds,
          spotlightMediaType: "WHITEBOARD",
        };
      }
    } else {
      if (meetingLayout === meetingLayouts.SPOTLIGHT) {
        obj = {
          spotlightParticipantId: null,
          sideBarPinnedParticipantIds: pinnedParticipantIds,
          spotlightMediaType: null,
        };
      } else {
        const spotlightParticipantId = pinnedParticipantIds[0];
        pinnedParticipantIds.splice(0, 1);

        obj = {
          spotlightParticipantId: spotlightParticipantId,
          sideBarPinnedParticipantIds: pinnedParticipantIds,
          spotlightMediaType: "WEBCAM",
        };
      }
    }

    if (mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR) {
      const splicesActiveParticipants = obj[
        "sideBarPinnedParticipantIds"
      ].splice(0, 4);

      obj["sideBarPinnedParticipantIds"] = splicesActiveParticipants;
    }

    const participantsCount = obj.sideBarPinnedParticipantIds?.length;

    if (participantsCount) {
      const pinnedParticipantsMap = new Map(pinnedParticipants);

      if (obj["spotlightMediaType"] === "WEBCAM") {
        if (obj["spotlightParticipantId"]) {
          pinnedParticipantsMap.delete(obj["spotlightParticipantId"]);
        }
      }

      const gridInfo = getGridRowsAndColumns({
        participantsCount,
        isMobile:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? false
            : isMobile,
        isTab:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? false
            : isTab,
        isSMDesktop:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? false
            : isSMDesktop,
        isLGDesktop:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? true
            : isLGDesktop,
        isLandscape:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? true
            : !isPortrait,
        isPresenting:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? false
            : !!obj.spotlightParticipantId,
      });

      const { singleRow } = getGridForMainParticipants({
        participants: localAndPinnedOnTop({
          localParticipantId,
          participants: [...obj.sideBarPinnedParticipantIds],
          pinnedParticipantIds: [...pinnedParticipantsMap.keys()],
          moveLocalUnpinnedOnTop: false,
        }),
        gridInfo,
      });

      obj["singleRow"] = singleRow;
    } else {
      obj["singleRow"] = [];
    }

    return obj;
  }, [
    mobilePortrait,
    pinnedParticipants,
    meetingLayout,
    presenterId,
    localParticipantId,
    isMobile,
    isTab,
    isSMDesktop,
    isLGDesktop,
    isPortrait,
    whiteboardStarted,
    isRecorder,
    layoutGridSize,
  ]);

  const mainScreenViewActive = useMemo(
    () =>
      presenterId ||
      whiteboardStarted ||
      meetingLayout === meetingLayouts.UNPINNED_SIDEBAR ||
      meetingLayout === meetingLayouts.UNPINNED_SPOTLIGHT,
    [presenterId, whiteboardStarted, meetingLayout]
  );

  const { singleRow, mainLayoutParticipantId } = useMemo(() => {
    let mainLayoutParticipantId;

    let _pinnedParticipants = new Map(pinnedParticipants);

    let mainParticipants = [...mainViewParticipants];

    if (presenterId || whiteboardStarted) {
      const remainingParticipants = [...participants.keys()].filter(
        (pId) => mainParticipants.findIndex((id) => id === pId) === -1
      );

      mainParticipants = [...mainParticipants, ...remainingParticipants];
    }

    if (hideLocalParticipant) {
      mainParticipants = mainParticipants.filter(
        (id) => id !== localParticipantId
      );

      _pinnedParticipants.delete(localParticipantId);
      _pinnedParticipants = new Map(_pinnedParticipants);
    }

    if (meetingLayout === meetingLayouts.UNPINNED_SIDEBAR) {
      if (!(!!presenterId || !!whiteboardStarted)) {
        if (_pinnedParticipants.size === 0) {
          if (activeSpeakerId) {
            mainParticipants = mainParticipants.filter(
              (id) => id !== activeSpeakerId
            );
            mainLayoutParticipantId = activeSpeakerId;
          } else {
            mainParticipants = mainParticipants.filter(
              (id) => id !== lastActiveParticipantId
            );
            mainLayoutParticipantId = lastActiveParticipantId;
          }
        } else {
          if (activeSpeakerId) {
            const pinnedActiveSpeaker =
              _pinnedParticipants.get(activeSpeakerId);

            if (pinnedActiveSpeaker) {
              mainParticipants = mainParticipants.filter(
                (id) => id !== activeSpeakerId
              );

              _pinnedParticipants.delete(activeSpeakerId);
              _pinnedParticipants = new Map(_pinnedParticipants);

              mainLayoutParticipantId = activeSpeakerId;
            } else {
              const firstPinnedParticipantId = _pinnedParticipants
                .keys()
                .next().value;

              mainParticipants = mainParticipants.filter(
                (id) => id !== firstPinnedParticipantId
              );

              _pinnedParticipants.delete(firstPinnedParticipantId);
              _pinnedParticipants = new Map(_pinnedParticipants);

              mainLayoutParticipantId = firstPinnedParticipantId;
            }
          } else {
            const lastActivePinnedParticipant = _pinnedParticipants.get(
              lastActiveParticipantId
            );

            if (lastActivePinnedParticipant) {
              mainParticipants = mainParticipants.filter(
                (id) => id !== lastActiveParticipantId
              );
              _pinnedParticipants.delete(lastActiveParticipantId);
              _pinnedParticipants = new Map(_pinnedParticipants);

              mainLayoutParticipantId = lastActiveParticipantId;
            } else {
              const firstPinnedParticipantId = _pinnedParticipants
                .keys()
                .next().value;

              mainParticipants = mainParticipants.filter(
                (id) => id !== firstPinnedParticipantId
              );

              _pinnedParticipants.delete(firstPinnedParticipantId);
              _pinnedParticipants = new Map(_pinnedParticipants);

              mainLayoutParticipantId = firstPinnedParticipantId;
            }
          }
        }
      } else {
        if (_pinnedParticipants.size === 0) {
          if (activeSpeakerId) {
            let activeIndex = mainParticipants.findIndex(
              (id) => id === activeSpeakerId
            );

            if (activeIndex !== -1) {
              const firstMainParticipantId = mainParticipants[0];
              mainParticipants[0] = activeSpeakerId;
              mainParticipants[activeIndex] = firstMainParticipantId;

              _pinnedParticipants.delete(firstMainParticipantId);
              _pinnedParticipants = new Map(_pinnedParticipants);
            } else {
              const mainGridRemovedParticipantId = mainParticipants.pop();

              mainParticipants[0] = activeSpeakerId;

              _pinnedParticipants.delete(mainGridRemovedParticipantId);
              _pinnedParticipants = new Map(_pinnedParticipants);
            }
          } else {
          }
        } else {
          if (activeSpeakerId) {
          } else {
          }
        }
      }
    } else if (meetingLayout === meetingLayouts.UNPINNED_SPOTLIGHT) {
      if (!!presenterId || !!whiteboardStarted) {
        mainParticipants = [];
      } else {
        mainParticipants = [];
        mainLayoutParticipantId = activeSpeakerId || lastActiveParticipantId;
      }
    }

    let participantsCount = mainParticipants?.length;

    if (participantsCount > layoutGridSize) {
      mainParticipants = mainParticipants.slice(0, layoutGridSize);
      const remainingMainParticipants = mainParticipants.splice(layoutGridSize);

      remainingMainParticipants.forEach((p) => {
        _pinnedParticipants.delete(p);
      });

      _pinnedParticipants = new Map(_pinnedParticipants);

      participantsCount = mainParticipants?.length;
    }

    if (mainScreenViewActive && participantsCount > sideStackSize) {
      mainParticipants = mainParticipants.slice(0, sideStackSize);
      const remainingMainParticipants = mainParticipants.splice(sideStackSize);

      remainingMainParticipants.forEach((p) => {
        _pinnedParticipants.delete(p);
      });

      _pinnedParticipants = new Map(_pinnedParticipants);

      participantsCount = mainParticipants?.length;
    }

    const gridInfo = getGridRowsAndColumns({
      participantsCount,
      isMobile,
      isTab,
      isSMDesktop,
      isLGDesktop,
      isLandscape: !isPortrait,
      isPresenting: !!mainScreenViewActive,
    });

    const { singleRow } = getGridForMainParticipants({
      participants: localAndPinnedOnTop({
        localParticipantId: hideLocalParticipant ? null : localParticipantId,
        participants: mainParticipants,
        pinnedParticipantIds:
          meetingLayout === meetingLayouts.UNPINNED_SIDEBAR ||
            meetingLayout === meetingLayouts.UNPINNED_SPOTLIGHT
            ? []
            : [..._pinnedParticipants.keys()],
        moveLocalUnpinnedOnTop:
          _pinnedParticipants.size && meetingLayout !== meetingLayouts.GRID
            ? false
            : true,
      }),
      gridInfo,
    });

    return { singleRow, mainLayoutParticipantId };
  }, [
    meetingLayout,
    participants,
    mainViewParticipants,
    isPortrait,
    isMobile,
    isTab,
    isSMDesktop,
    isLGDesktop,
    presenterId,
    whiteboardStarted,
    localParticipantId,
    pinnedParticipants,
    activeSpeakerId,
    lastActiveParticipantId,
    mainParticipantId,
    layoutGridSize,
    hideLocalParticipant,
    sideStackSize,
    mainScreenViewActive,
  ]);

  const spacing = reduceEdgeSpacing ? 4 : rowSpacing - gutter;
  console.log("rowSpacing", rowSpacing)
  console.log("spacing", spacing)

  const theme = useTheme();

  const _presentingSideBarWidth = useResponsiveSize({
    xl: 320,
    lg: 280,
    md: 260,
    sm: 240,
    xs: 200,
  });

  const isVeryLargeScreen = useMediaQuery({ query: "(min-width: 2560px)" });
  const isUltraLargeScreen = useMediaQuery({ query: "(min-width: 3840px)" });

  const presentingSideBarWidth = useMemo(() => {
    if (isUltraLargeScreen) {
      return Math.round(_presentingSideBarWidth * 2);
    } else if (isVeryLargeScreen) {
      return Math.round(_presentingSideBarWidth * 1.5);
    }
    return _presentingSideBarWidth;
  }, [_presentingSideBarWidth, isVeryLargeScreen, isUltraLargeScreen]);

  usePubSub("UPDATE_LOGO", createLogoUpdateHandlers(() => {
    // Logo position handler - currently just logging
    console.log('Logo update received in MainView');
  }));

  usePubSub("UPDATE_MEETING_BACKGROUND", createBackgroundUpdateHandlers(setMeetingBackground));

  const mainContainerHorizontalPadding = useMemo(
    () =>
      reduceEdgeSpacing
        ? 0
        : presenterId ||
          whiteboardStarted ||
          (mainLayoutParticipantId && singleRow.length !== 0)
          ? 0
          : typeof sideBarMode === "string"
            ? 0
            : mainViewParticipants?.length <= 9
              ? isLGDesktop
                ? !mainScreenViewActive && singleRow.length === 2
                  ? 0
                  : 140
                : isSMDesktop
                  ? !mainScreenViewActive && singleRow.length === 2
                    ? 20
                    : 90
                  : isTab && !isPortrait
                    ? 60
                    : 0
              : 0,
    [
      mainScreenViewActive,
      sideBarMode,
      mainViewParticipants,
      isLGDesktop,
      isSMDesktop,
      singleRow,
      isTab,
      isPortrait,
      whiteboardStarted,
      presenterId,
      mainLayoutParticipantId,
      meetingLayout,
      reduceEdgeSpacing,
    ]
  );

  const gridVerticalSpacing = useResponsiveSize({
    xl: 160,
    lg: 90,
    md: 90,
    sm: 60,
    xs: 60,
  });

  const actualPresentingSideBarWidth = useMemo(
    () => (singleRow.length ? presentingSideBarWidth : 0),
    [singleRow, presentingSideBarWidth]
  );

  const [meetingOverlay, setMeetingOverlay] = useState("");
  const audioRef = useRef(null);

  usePubSub("UPDATE_MEETING_OVERLAY", createOverlayUpdateHandlers(setMeetingOverlay));
  usePubSub("UPDATE_MUSIC", createMusicUpdateHandlers(audioRef));
  usePubSub("UPDATE_BANNER", createBannerUpdateHandlers(setBannerName, setBannerId));

  const getPaddingStyles = (meetingOverlay, bannerName, meetingLayout, singleRow, meetingLayouts) => {
    const hasOverlayOrBanner = meetingOverlay || bannerName;

    if (!hasOverlayOrBanner) {
      return {
        paddingLeft: 0,
        paddingRight: 0,
      };
    }

    const isUnpinnedWithSingleRow =
      meetingLayout === meetingLayouts.UNPINNED_SIDEBAR && singleRow.length > 0;

    return {
      paddingLeft: 0,
      paddingRight: 0,
    };
  };

  const { paddingLeft, paddingRight } = getPaddingStyles(
    meetingOverlay,
    bannerName,
    meetingLayout,
    singleRow,
    meetingLayouts
  );

  // Helper functions to reduce cognitive complexity
  const calculateSidebarOffset = () => {
    const sidebarWidth = Number(localStorage.getItem('ProSidebarWidth')) || 80;
    return sidebarWidth * 2.5;
  };
  const calculateHorizontalPadding = () => {
    const shouldAddExtraPadding = 
      !mainScreenViewActive &&
      singleRow.length > 12 &&
      singleRow.length < 17 &&
      typeof sideBarMode !== "string";

    if (mainContainerHorizontalPadding === 0) {
      return shouldAddExtraPadding ? '16vh' : '0vh';
    }

    const extraPadding = shouldAddExtraPadding ? '16vh' : '0';
    return `calc(${pxToVh(mainContainerHorizontalPadding)} + ${extraPadding})`;
  };

  const calculateInnerWidth = () => {
    const sidebarOffset = calculateSidebarOffset();

    if (mobilePortrait) {
      return width - 2 * spacing;
    }

    if (!spotlightParticipantId) {
      return 0;
    }

    let baseWidth = width;
    if (sideBarPinnedParticipantIds?.length > 0) {
      baseWidth = width - presentingSideBarWidth - sidebarOffset;
    }
    baseWidth -= 2 * spacing;

    if (singleRow.length === 0) {
      const sideBarAdjustment = sideBarData?.type === true ? spacing * 9 : -spacing * 2;
      baseWidth -= (2 * mainContainerHorizontalPadding) + sideBarAdjustment;
    }

    return baseWidth;
  };

  const calculateContainerWidth = () => {
    const innerWidthCalc = calculateInnerWidth();

    if (whiteboardStarted && sideBarPinnedParticipantIds?.length > 0) {
      if (typeof sideBarMode === "string") {
        return width - 2 * spacing;
      }
      return width - (isMobile ? 0 : presentingSideBarWidth) - 2 * spacing;
    }

    return innerWidthCalc;
  };

  const calculateWhiteboardBaseWidth = () => {
    
    if (singleRow.length === 0) {
    return width - 2 * spacing;
  }
    if (typeof sideBarMode === "string") {
      return width - 2 * spacing;
    }
    if (sideBarPinnedParticipantIds?.length > 0) {
      return width - (isMobile ? 0 : presentingSideBarWidth) - 2 * spacing;
    }
    return width - 2 * spacing;
  };

  const calculateWhiteboardDimensions = () => {
    const baseWidth = calculateWhiteboardBaseWidth();
    const whiteboardWidth = baseWidth -
      (whiteboardToolbarWidth + 2 * whiteboardSpacing) -
      (whiteboardToolbarWidth === 0 ? 2 * 16 : 0);

    const dims = convertHWAspectRatio({
      height: height - 2 * spacing - (whiteboardToolbarWidth === 0 ? 2 * 16 : 0),
      width: whiteboardStarted ? whiteboardWidth : 0,
    });

    return {
      ...dims,
      whiteboardToolbarWidth,
      whiteboardSpacing,
      originalHeight: height,
      originalWidth: baseWidth,
    };
  };

  const getScreenSharePadding = () => {
    if (!presenterId) return 0;
    return (meetingBackground || meetingOverlay) ? '1.2vh' : '0.8vh';
  };

  const getQualitySetting = () => {
    if (!meetingResolution) return "s2t2";
    if (meetingResolution === meetingResolutions.SD) return "s1t2";
    if (meetingResolution === meetingResolutions.HD) return "s2t2";
    return "s2t2";
  };

  const renderMainContent = () => {
    if (whiteboardStarted) {
      return <WhiteboardContainer {...calculateWhiteboardDimensions()} />;
    }

    if (presenterId) {
      return (
        <div
          style={{
            position: 'relative',
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: '100%',
            width: '100%',
          }}
        >
          <PresenterView presenterId={presenterId} />
        </div>
      );
    }

    if (mainLayoutParticipantId) {
      return (
        <div
          style={{
            position: "relative",
            transition: `height ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
            transitionTimingFunction: "ease-in-out",
            height: '100%',
            width: meetingLayout === 'UNPINNED_SIDEBAR' && singleRow.length > 0 ? '100%' : '100%',
          }}
        >
          <MemoizedMotionParticipant
            participantId={mainLayoutParticipantId}
            gutter={gutter}
            quality={getQualitySetting()}
            relativeHeight={100}
            relativeWidth={100}
            relativeTop={0}
            relativeLeft={0}
            meetingBackground={meetingBackground}
            key={`mainLayoutParticipantId_${mainLayoutParticipantId}`}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <>

      {
        participants && (
          <div
            style={{
              position: 'absolute',
              // height: height,
              height: '100%',
              // width: width - (6 * spacing),
              width: '100%',
              // width: (spotlightParticipantId ? width : 0) - (singleRow.length === 0 ? 2 * mainContainerHorizontalPadding : 0),
              transition: `all ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
              transitionTimingFunction: "ease-in-out",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center"

            }}
          >
            <div
              style={{
                // height: height,
                height: '100%',
                // width: width - (8 * spacing),
                width: '100%',
                // width: mobilePortrait
                //   ? width - 2 * spacing
                //   : (spotlightParticipantId
                //     ? '100%'
                //     : 0) - (singleRow.length === 0 ? 2 * mainContainerHorizontalPadding : 0),
                transition: `width ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                transitionTimingFunction: "ease-in-out",
                borderRadius: theme.spacing(1),
                position: "relative",
                display: "flex", // Add this to create a row layout
                alignItems: whiteboardStarted ? "flex-start" : "center",
                justifyContent: "flex-start",
                flexDirection: "row", // Make sure items align horizontally
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft,
                paddingRight,
              }}
            >
              {/* Main content area - whiteboard, presenter, or speaker */}
              <div
                style={{
                  width: (() => {
                    if (!mainScreenViewActive) return '0px';
                    return singleRow.length > 0 ? 'auto' : '100%';
                  })(),
                  flex: (() => {
                    if (mainScreenViewActive && singleRow.length > 0) return '1 1 0%';
                    if (mainScreenViewActive) return '1 1 auto';
                    return '0 0 auto';
                  })(),
                  height: '100%',
                  transition: `flex ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                  transitionTimingFunction: "ease-in-out",
                  // paddingLeft: presenterId ? spacing : 0,
                  // paddingTop: presenterId ? spacing : 0,
                  padding: getScreenSharePadding(),
                  display: "flex",
                  minWidth: 0,
                  position: "relative",
                }}
              >
                {whiteboardStarted && (
                  <Box
                    style={{
                      position: "absolute",
                      top: typeof whiteboardSpacing === 'number' ? `${whiteboardSpacing}px` : whiteboardSpacing,
                      right: typeof whiteboardSpacing === 'number' ? `${whiteboardSpacing}px` : whiteboardSpacing,
                      zIndex: 10000,
                      pointerEvents: "auto",
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        mMeeting.meeting?.stopWhiteboard?.();
                      }}
                      style={{
                        backgroundColor: theme.palette.lightTheme.three,
                        padding: "0.74vh",
                        margin: 0,
                      }}
                    >
                      <CloseIcon
                        style={{
                          width: "1.85vh",
                          height: "1.85vh",
                          color:
                            appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
                              ? "#95959E"
                              : theme.palette.common.black,
                        }}
                      />
                    </IconButton>
                  </Box>
                )}
                <div
                  style={{
                    width: calculateContainerWidth(),
                    height: '100%',
                    transition: `width ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                    transitionTimingFunction: "ease-in-out",
                    borderRadius: theme.spacing(1),
                    overflow: "hidden",
                    position: "relative",
                    flex: '1 1 0px',
                    display: 'flex',
                    alignItems: whiteboardStarted ? 'flex-start' : 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  {renderMainContent()}
                </div>
              </div>
              {
                (!presenterId && ((isMobile && mainScreenViewActive) ? null : singleRow.length <=
                  0 ? null : (
                  <div
                    style={{
                      // backgroundColor:
                      //   appTheme === appThemes.DARK
                      //     ? theme.palette.darkTheme.main
                      //     : appTheme === appThemes.LIGHT
                      //       ? theme.palette.lightTheme.main
                      //       : theme.palette.background.default,
                      // overflowX: "hidden",
                      overflowY: mainScreenViewActive ? "scroll" : "hidden",
                      // width: mainScreenViewActive
                      //   ? actualPresentingSideBarWidth
                      //   : width - 2 * spacing,
                      // height: '90vh',
                      height: '100%',
                      width: mainScreenViewActive && singleRow.length > 0 ? undefined : '100%',
                      flex: mainScreenViewActive && singleRow.length > 0 
                        ? `0 0 ${presentingSideBarWidth}px`
                        : '1 1 auto',
                      // width:'20%',
                      // margin: spacing,
                      transition: `all ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                      transitionTimingFunction: "ease-in-out",
                      minWidth: 0,
                      paddingLeft: calculateHorizontalPadding(),
                      paddingRight: calculateHorizontalPadding(),
                      paddingTop:
                        singleRow.length === 2 && !mainScreenViewActive && !isMobile
                          ? gridVerticalSpacing
                          : 0,
                      // padding: meetingOverlay && '3% 0%',
                      overflowY: "scroll",
                    }}
                  >
                    <div
                      style={{
                        // height:
                        //   (mainScreenViewActive
                        //     ? (singleRow.length * actualPresentingSideBarWidth * 2) / 4
                        //     : height - 2 * spacing) -
                        //   (singleRow.length === 2 && !mainScreenViewActive && !isMobile
                        //     ? 2 * gridVerticalSpacing
                        //     : 0),
                        height: (() => {
                          const calculateBaseHeight = () => {
                            if (!mainScreenViewActive) {
                              return height - 2 * spacing;
                            }
                            
                            return (singleRow.length * actualPresentingSideBarWidth * 2) / 3;
                          };

                          const baseHeight = calculateBaseHeight();
                          const verticalSpacingAdjustment = 
                            (singleRow.length === 2 && !mainScreenViewActive && !isMobile)
                              ? 2 * gridVerticalSpacing
                              : 0;
                          
                          const calculatedHeight = baseHeight - verticalSpacingAdjustment;
                          
                          console.log("[SIDEBAR_CONTAINER_HEIGHT]", {
                            meetingLayout,
                            mainScreenViewActive,
                            singleRowLength: singleRow.length,
                            meetingOverlay: !!meetingOverlay,
                            actualPresentingSideBarWidth,
                            calculatedHeight,
                            heightProp: height,
                            spacing,
                          });
                          
                          return calculatedHeight;
                        })(),
                        position: "relative",
                        transition: `height ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                        transitionTimingFunction: "ease-in-out",
                        zIndex: meetingOverlay || transcriptionState == "TRANSCRIPTION_STARTED" ? "900" : "1000",
                      }}
                    >
                      {/* {singleRow.map((c) => ( */}
                      {((meetingLayout === meetingLayouts.UNPINNED_SIDEBAR && meetingOverlay) ? singleRow.slice(0, 4) : singleRow).map((c) => (
                        <MemoizedMotionParticipant
                          quality={
                            meetingResolution
                              ? meetingResolution === meetingResolutions.SD
                                ? "s1t2"
                                : meetingResolution === meetingResolutions.HD && "s2t2"
                              : calcQuality(mainViewParticipants?.length || 1)
                          }
                          key={`main_participant_${c.participantId}`}
                          {...c}
                          gutter={gutter}
                          meetingBackground={meetingBackground}
                          useVisibilitySensor={mainScreenViewActive ? true : false}
                        />
                      ))}
                    </div>
                  </div>
                )))
              }
            </div>
          </div>
        )
      }

      {/* OLDLOGIC */}
      {/* <div
        style={{
          // width: presenterId
          //   ? width - presentingSideBarWidth
          //   : 0,
          // height: !presenterId ? height / 2 : height,
          transition: `all ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
          transitionTimingFunction: "ease-in-out",
          // paddingLeft: spacing,
          // paddingTop: spacing,
          width: '100%',
          height: '100%',
        }}
      >
        <div
          style={{
            height: height - 2 * spacing,
            // width: mobilePortrait
            //   ? width - 2 * spacing
            //   : (spotlightParticipantId
            //     ? (sideBarPinnedParticipantIds?.length > 0
            //       ? width
            //       : width) -
            //     2 * spacing
            //     : 0) -
            //   (singleRow.length === 0
            //     ? 2 * mainContainerHorizontalPadding
            //     : 0),
            width: mobilePortrait
              ? width - 2 * spacing
              : (spotlightParticipantId
                ? width - 2 * spacing
                : 0) - (singleRow.length === 0 ? 2 * mainContainerHorizontalPadding : 0),
            // height: height,
            // backgroundColor:
            //   presenterId || whiteboardStarted
            //     ? appTheme === appThemes.DARK
            //       ? theme.palette.darkTheme.slightLighter
            //       : appTheme === appThemes.LIGHT
            //         ? theme.palette.lightTheme.two
            //         : theme.palette.background.paper
            //     : undefined,
            // backgroundColor:'red',
            transition: `width ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
            transitionTimingFunction: "ease-in-out",
            borderRadius: theme.spacing(1),
            // overflow: "hidden",
            position: "relative",

          }}
        >
          {whiteboardStarted && (
            <WhiteboardContainer
              {...{
                ...convertHWAspectRatio({
                  height:
                    height -
                    2 * spacing -
                    (whiteboardToolbarWidth === 0 ? 2 * 16 : 0),
                  width: whiteboardStarted
                    ? width -
                    (isMobile ? 0 : actualPresentingSideBarWidth) -
                    2 * spacing -
                    (whiteboardToolbarWidth + 2 * whiteboardSpacing) -
                    (whiteboardToolbarWidth === 0 ? 2 * 16 : 0)
                    : 0,
                }),
                whiteboardToolbarWidth,
                whiteboardSpacing,
                originalHeight: height - 2 * spacing,
                originalWidth: whiteboardStarted
                  ? width -
                  (isMobile ? 0 : actualPresentingSideBarWidth) -
                  2 * spacing
                  : 0,
              }}
            />
          )}

          {presenterId && <PresenterView presenterId={presenterId} />}

          {!presenterId && !whiteboardStarted && mainLayoutParticipantId && (

            <div
              style={{
                // position: "absolute",
                // top: 0,
                // bottom: 0,
                // left:
                //   singleRow.length === 0 ? mainContainerHorizontalPadding : 0,
                // right:
                //   singleRow.length === 0 ? mainContainerHorizontalPadding : 0,
                position: "relative",
                transition: `height ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                transitionTimingFunction: "ease-in-out",
                height: '100%',
                width: '100%',
                // width: sideBarData.type === true ? '89%' : '100%',
              }}
            >
              <MemoizedMotionParticipant
                {...{
                  participantId: mainLayoutParticipantId,
                  gutter,
                  quality: meetingResolution
                    ? meetingResolution === meetingResolutions.SD
                      ? "s1t2"
                      : meetingResolution === meetingResolutions.HD && "s2t2"
                    : "s2t2",
                  relativeHeight: 100,
                  relativeWidth: 100,
                  relativeTop: 0,
                  relativeLeft: 0,
                  meetingBackground,
                }}
                key={`mainLayoutParticipantId_${mainLayoutParticipantId}`}
              />
            </div>


          )}

        </div>
      </div> */}
      {/* {
        isMobile && mainScreenViewActive ? null : singleRow.length <=
          0 ? null : (
          <div
            style={{
              backgroundColor:
                appTheme === appThemes.DARK
                  ? theme.palette.darkTheme.main
                  : appTheme === appThemes.LIGHT
                    ? theme.palette.lightTheme.main
                    : theme.palette.background.default,
              overflowX: "hidden",
              overflowY: mainScreenViewActive ? "scroll" : "hidden",
              width: mainScreenViewActive
                ? actualPresentingSideBarWidth
                : width - 2 * spacing,
              // height: '90vh',
              height: '100%',
              // margin: spacing,
              transition: `all ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
              transitionTimingFunction: "ease-in-out",
              paddingLeft:
                mainContainerHorizontalPadding === 0
                  ? (!mainScreenViewActive &&
                    singleRow.length > 12 &&
                    singleRow.length < 17 &&
                    typeof sideBarMode !== "string"
                    ? '16vh'
                    : 0)
                  : `calc(${pxToVh(mainContainerHorizontalPadding)} + ${
                      !mainScreenViewActive &&
                      singleRow.length > 12 &&
                      singleRow.length < 17 &&
                      typeof sideBarMode !== "string"
                        ? '16vh'
                        : '0'
                    })`,
              paddingRight:
                mainContainerHorizontalPadding === 0
                  ? (!mainScreenViewActive &&
                    singleRow.length > 12 &&
                    singleRow.length < 17 &&
                    typeof sideBarMode !== "string"
                    ? '16vh'
                    : 0)
                  : `calc(${pxToVh(mainContainerHorizontalPadding)} + ${
                      !mainScreenViewActive &&
                      singleRow.length > 12 &&
                      singleRow.length < 17 &&
                      typeof sideBarMode !== "string"
                        ? '16vh'
                        : '0'
                    })`,
              paddingTop:
                singleRow.length === 2 && !mainScreenViewActive && !isMobile
                  ? gridVerticalSpacing
                  : 0,
              marginLeft: "auto",
            }}
          >
            <div
              style={{
                height:
                  (mainScreenViewActive
                    ? (singleRow.length * actualPresentingSideBarWidth * 2) / 3
                    : height) -
                  2 * spacing -
                  (singleRow.length === 2 && !mainScreenViewActive && !isMobile
                    ? 2 * gridVerticalSpacing
                    : 0),
                position: "relative",
                transition: `height ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                transitionTimingFunction: "ease-in-out",
              }}
            >
              {singleRow.map((c) => (
                <MemoizedMotionParticipant
                  quality={
                    meetingResolution
                      ? meetingResolution === meetingResolutions.SD
                        ? "s1t2"
                        : meetingResolution === meetingResolutions.HD && "s2t2"
                      : calcQuality(mainViewParticipants?.length || 1)
                  }
                  key={`main_participant_${c.participantId}`}
                  {...c}
                  gutter={gutter}
                  useVisibilitySensor={mainScreenViewActive ? true : false}
                />
              ))}
            </div>
          </div>
        )
      } */}
      {/* </div> */}
    </>
  )
  // : null;
};

export default React.memo(
  MainViewContainer,
  (prevProps, nextProps) =>
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.whiteboardToolbarWidth === nextProps.whiteboardToolbarWidth &&
    prevProps.whiteboardSpacing === nextProps.whiteboardSpacing
);
